import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H2 from '@base/H2/H2';
import Image from '@base/Image';
import { TTU_NUMBER_CLICKED, TTU_POP_UP_CLICK } from '@constants/segment';
import { TalkState } from '@cont/Login/constants';
import { sendTTUEvents } from '@events/sendAnalytics';
import { evtNameCategoryMap, inboundCallNumber } from '@constants/index';
import { Spacings, mediaQuery } from '@styles/index';
import {
  getTTUShowData,
  getShowTTUCurtain
} from '@stores/selectors/ttuSelectors';
import Call from '@icon/Call';
import H6 from '@base/H6/H6';
import { useCallback } from 'react';
import { segment } from '@actions/analytics';
import useTTUStore from '@stores/ttu';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import { emptyObject } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';

const TTUContainer = styled.div`
  margin: ${Spacings.SPACING_12B} auto;
  position: relative;
  display: flex;
  width: var(--layout-width);
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
    margin: ${Spacings.SPACING_8B} auto;
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
    align-items: flex-start;
  }
  justify-content: space-between;
`;

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${Spacings.SPACING_8B};
  margin-right: ${Spacings.SPACING_25B};
  ${mediaQuery.uptoMobile} {
    margin-right: 0px !important;
  }
`;

const IllustrationContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  margin-top: -${Spacings.SPACING_2B};
`;

const HaveQuestionAboutGoalHeading = styled(H2)`
  color: var(--color-text-primary);
  width: 60%;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Options = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
    flex-direction: column;
  }
`;

const Divider = styled.div`
  width: 0;
  height: ${Spacings.SPACING_8B};
  align-self: center;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  ${mediaQuery.uptoMobile} {
    width: var(--layout-mweb-width);
    flex-direction: column;
  }
`;

const Phone = styled.a`
  display: flex;
  width: fit-content;
`;
const PhoneWrapper = styled.div`
  align-self: center;
`;
const CallWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    flex-direction: row;
    align-items: center;
  }
`;
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    width: 60%;
  }
`;
const StyledButton = styled(Button)`
  width: 234px;
  ${mediaQuery.uptoMobile} {
    width: var(--layout-mweb-width);
  }
`;
const Banner = ({ goalDetails, className, ttuConfig = {} }) => {
  const isMobile = useIsMobile();

  const {
    subTitle = 'Our expert can answer all your questions',
    ttuEventsProps = emptyObject,
    isOffline = false
  } = ttuConfig;
  const showData = useTTUStore(getTTUShowData);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const meData = useMyInfo(getMe);

  let userType = showData?.isRepeatUser ? 'Repeat' : 'New';
  if (
    !showData?.isRepeatUser &&
    (showData?.state === TalkState.NO_SHOW ||
      showData?.state === TalkState.LOCAL_NO_SHOW)
  ) {
    userType = 'Scheduled';
  }
  const phoneNumber =
    showData?.countryCode && showData?.phoneNumber
      ? `${showData?.countryCode} ${showData?.phoneNumber}`
      : inboundCallNumber;

  const onPhoneNumberClick = useCallback(() => {
    segment.track(
      TTU_NUMBER_CLICKED,
      {
        inbound_phone_number: phoneNumber,
        ...ttuEventsProps
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
  }, [phoneNumber, ttuEventsProps]);
  return (
    <TTUContainer className={className}>
      <AboutWrapper>
        <HeaderWrapper>
          <HaveQuestionAboutGoalHeading className="ttu-title">
            <Typography variant="h3">
              Have questions about the subscriptions ?
            </Typography>
          </HaveQuestionAboutGoalHeading>
          <Typography variant="p1" color="var(--color-i-gray-13)">
            {subTitle}
          </Typography>
        </HeaderWrapper>
        <Options>
          <ButtonsWrapper>
            <StyledButton
              label="Get a call from us"
              variant="secondary"
              size="large"
              onClick={() => {
                sendTTUEvents(
                  TTU_POP_UP_CLICK,
                  {
                    user_ttu_status: userType,
                    source: 'Bottom Home Screen',
                    last_primary_source_section: 'Bottom Home Screen',
                    ...ttuEventsProps
                  },
                  meData?.uid,
                  meData?.username,
                  goalDetails?.uid,
                  goalDetails?.name
                );
                if (goalDetails?.uid) {
                  showTTUCurtain(true, 'Bottom Home Screen');
                } else {
                  window.open(`/talktous?isOffline=${isOffline}`, '_blank');
                }
              }}
            />
          </ButtonsWrapper>
          {phoneNumber && (
            <>
              <Divider />
              <PhoneWrapper>
                <CallWrapper>
                  <Typography variant="h6" color="var(--color-i-gray-13)">
                    OR CALL US
                  </Typography>
                  <Phone
                    href={`tel:${phoneNumber}`}
                    onClick={onPhoneNumberClick}
                  >
                    <Call
                      size={Spacings.SPACING_5B}
                      viewBox="0 -4 24 24"
                      color="var(--color-text-primary)"
                    />
                    <H6>{phoneNumber}</H6>
                  </Phone>
                </CallWrapper>
              </PhoneWrapper>
            </>
          )}
        </Options>
      </AboutWrapper>
      <IllustrationContainer>
        <Image
          src="TTU_illustration.svg"
          width={isMobile ? 80 : 180}
          height={isMobile ? 80 : 180}
          alt="illustration"
        />
      </IllustrationContainer>
    </TTUContainer>
  );
};

export default Banner;

Banner.propTypes = {
  goalDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string
};

Banner.defaultProps = {
  className: ''
};
